import { ReactNode } from "react";
import styled from "styled-components";
import { EditIcon } from "../../assets/icons/editIcon";
import { Button, ButtonVariant, CancelButton } from "./Button";
import { DialogSize, Modal, ModalHeight } from "./Dialog";
import { designSystemVars as vars } from "../tokens";

export type ActionModalPresentation =
  | "standard"
  | "compact"
  | "form"
  | "selection"
  | "editor";

export interface ActionModalProps {
  open?: boolean;
  title: ReactNode;
  children: ReactNode;
  onOpenChange: (open: boolean) => void;
  onAction?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  actionLabel?: ReactNode;
  actionIcon?: ReactNode;
  actionIntent?: Extract<ButtonVariant, "primary" | "danger" | "ai">;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  cancelLabel?: ReactNode;
  cancelLoading?: boolean;
  cancelDisabled?: boolean;
  showActions?: boolean;
  closeOnAction?: boolean;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  size?: DialogSize;
  height?: ModalHeight;
  presentation?: ActionModalPresentation;
}

const presentationIntents = {
  standard: "action",
  compact: "compactAction",
  form: "form",
  selection: "selection",
  editor: "editor",
} as const;

const ActionButton = styled(Button)<{ $contentWidth: boolean }>`
  min-width: ${({ $contentWidth }) =>
    $contentWidth ? vars.spacing.none : "137px"};
  height: 40px;
`;

const ActionCancelButton = styled(CancelButton)<{ $contentWidth: boolean }>`
  min-width: ${({ $contentWidth }) =>
    $contentWidth ? vars.spacing.none : "137px"};
  height: 40px;
`;

export const ActionModal = ({
  open = true,
  title,
  children,
  onOpenChange,
  onAction,
  onCancel,
  actionLabel = "Edit",
  actionIcon = <EditIcon />,
  actionIntent = "primary",
  actionLoading = false,
  actionDisabled = false,
  cancelLabel = "Cancel",
  cancelLoading = false,
  cancelDisabled = false,
  showActions = true,
  closeOnAction = true,
  closeOnEscape = false,
  closeOnOverlayClick = false,
  size = "xs",
  height = "content",
  presentation = "standard",
}: ActionModalProps) => {
  const handleCancel = async () => {
    if (cancelLoading || cancelDisabled) return;

    await onCancel?.();
    onOpenChange(false);
  };

  const handleAction = async () => {
    if (actionLoading || actionDisabled) return;

    await onAction?.();
    if (closeOnAction) onOpenChange(false);
  };

  const footer = showActions ? (
    <>
      <ActionCancelButton
        $contentWidth={presentation === "selection"}
        size={presentation === "selection" ? "md" : "sm"}
        onClick={handleCancel}
        disabled={cancelDisabled}
        loading={cancelLoading}
        loadingLabel="Processing"
      >
        {cancelLabel}
      </ActionCancelButton>
      <ActionButton
        $contentWidth={presentation === "selection"}
        size={presentation === "selection" ? "md" : "sm"}
        variant={actionIntent === "danger" ? "dangerSolid" : actionIntent}
        onClick={handleAction}
        disabled={actionDisabled}
        loading={actionLoading}
        loadingLabel="Processing"
        leadingIcon={actionIcon}
      >
        {actionLabel}
      </ActionButton>
    </>
  ) : undefined;

  return (
    <Modal
      open={open}
      title={title}
      closeLabel="Dismiss dialog"
      onClose={handleCancel}
      footer={footer}
      size={size}
      height={height}
      intent={presentationIntents[presentation]}
      closeOnEscape={closeOnEscape}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      {children}
    </Modal>
  );
};
